if (!customElements.get('product-purchase-price')) {
  customElements.define(
    'product-purchase-price',
    class ProductPurchasePrice extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info');
        if (!this.productInfo) return;

        this.onetimePanel = this.querySelector('[data-purchase-panel="onetime"]');
        this.subscriptionPanel = this.querySelector('[data-purchase-panel="subscription"]');
        this.planData = this.getPlanData();
        this._lastStateKey = '';
        this._subifyReady = false;

        this.update = this.update.bind(this);
        this.handleDelegatedClick = this.handleDelegatedClick.bind(this);
        this.handleDelegatedChange = this.handleDelegatedChange.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);

        this.setupListeners();
        this.waitForSubify();
        this.scheduleUpdate();
      }

      disconnectedCallback() {
        this.teardownListeners();
        window.clearTimeout(this.updateTimer);
        this._waitTimers?.forEach((timer) => window.clearTimeout(timer));
      }

      scheduleUpdate() {
        window.clearTimeout(this.updateTimer);
        this.updateTimer = window.setTimeout(this.update, 80);
      }

      setupListeners() {
        this.productInfo.addEventListener('click', this.handleDelegatedClick);
        this.productInfo.addEventListener('change', this.handleDelegatedChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
        }
      }

      teardownListeners() {
        this.productInfo?.removeEventListener('click', this.handleDelegatedClick);
        this.productInfo?.removeEventListener('change', this.handleDelegatedChange);
        this.variantChangeUnsubscriber?.();
        this.injectObserver?.disconnect();
        this.subifyObserver?.disconnect();
      }

      waitForSubify() {
        if (this.dataset.hasSubscription !== 'true') return;

        const onSubifyReady = () => {
          if (!this.isConnected || this._subifyReady || !this.getSubifyWidget()) return;
          this._subifyReady = true;
          this.injectObserver?.disconnect();
          this.observeSubifyWidget();
          this._lastStateKey = '';
          this.scheduleUpdate();
        };

        if (this.getSubifyWidget()) {
          onSubifyReady();
          return;
        }

        this._waitTimers = [100, 300, 600, 1000, 1500, 2500, 4000, 6000].map((delay) =>
          window.setTimeout(onSubifyReady, delay)
        );

        this.injectObserver = new MutationObserver(onSubifyReady);
        this.injectObserver.observe(this.productInfo, { childList: true, subtree: true });
      }

      observeSubifyWidget() {
        const widget = this.getSubifyWidget();
        if (!widget || widget.dataset.purchasePriceObserved === 'true') return;

        widget.dataset.purchasePriceObserved = 'true';
        this.subifyObserver = new MutationObserver(() => {
          this._lastStateKey = '';
          this.scheduleUpdate();
        });
        this.subifyObserver.observe(widget, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      getPlanData() {
        try {
          return JSON.parse(this.querySelector('[data-subscription-plan-data]')?.textContent || '{}');
        } catch (error) {
          return {};
        }
      }

      getSectionId() {
        return this.productInfo?.dataset.originalSection || this.productInfo?.dataset.section;
      }

      getVariantId() {
        return this.productInfo.querySelector('input.product-variant-id, input[name="id"]')?.value;
      }

      getPlanId() {
        return this.productInfo.querySelector('input[name="selling_plan"]')?.value;
      }

      handleVariantChange(event) {
        if (event?.data?.sectionId !== this.getSectionId()) return;
        this.onetimePanel = this.querySelector('[data-purchase-panel="onetime"]');
        this.subscriptionPanel = this.querySelector('[data-purchase-panel="subscription"]');
        this.planData = this.getPlanData();
        this._lastStateKey = '';
        this._subifyReady = false;
        this.subifyObserver?.disconnect();
        this.waitForSubify();
        this.scheduleUpdate();
      }

      handleDelegatedClick(event) {
        if (event.target.closest('.subify-choice-box-wrapper, .subify-choice-wrapper, .checkbox-label')) {
          this._lastStateKey = '';
          this.scheduleUpdate();
        }
      }

      handleDelegatedChange(event) {
        if (event.target.matches('[name="selling_plan"], select[class*="selling"], select[name*="selling"]')) {
          this._lastStateKey = '';
          this.scheduleUpdate();
        }
      }

      getSubifyWidget() {
        return this.productInfo.querySelector(
          '.subify-widget-whole-wrapper, [class*="subify-widget-whole"], [class*="subify-widget"]'
        );
      }

      getActiveSubifyBox() {
        const widget = this.getSubifyWidget();
        if (!widget) return null;

        const activeLabel = widget.querySelector('.checkbox-label.active');
        if (activeLabel) {
          return activeLabel.closest('.subify-choice-box-wrapper');
        }

        const checkedInput = widget.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
        if (checkedInput) {
          return checkedInput.closest('.subify-choice-box-wrapper');
        }

        return null;
      }

      isOneTimeBox(box) {
        if (!box) return true;

        if (
          box.querySelector(
            'select[class*="selling"], select[name*="selling"], [class*="frequency"], [class*="delivery"]'
          )
        ) {
          return false;
        }

        const labelText = (box.querySelector('.checkbox-label')?.textContent || '').toLowerCase();
        if (/one[\s-]?time|just once/.test(labelText)) return true;
        if (/subscri|delivery frequency/.test(labelText)) return false;

        const boxes = box.parentElement?.querySelectorAll(':scope > .subify-choice-box-wrapper');
        if (boxes?.length === 2) return box === boxes[0];

        return true;
      }

      isSubscriptionMode() {
        if (this.dataset.hasSubscription !== 'true' || !this.getSubifyWidget()) return false;

        const activeBox = this.getActiveSubifyBox();
        if (activeBox) return !this.isOneTimeBox(activeBox);

        return !!this.getPlanId();
      }

      getSelectedPlan() {
        const plans = this.planData[this.getVariantId()] || [];
        if (!plans.length) return null;

        const planId = this.getPlanId();
        if (planId) {
          return plans.find((plan) => String(plan.plan_id) === String(planId)) || plans[0];
        }

        return plans[0];
      }

      formatDiscountLabel(percent) {
        const template = this.planData.discount_label || '__PERCENT__% OFF';
        return template.replace('__PERCENT__', percent);
      }

      normalizeMoneyHtml(html) {
        return (html || '').replace(/\s+/g, ' ').trim();
      }

      wrapPlainPriceAsMoneyHtml(plainText) {
        if (!plainText) return '';

        const trimmed = plainText.trim();
        if (trimmed.includes('<')) return trimmed;

        const match = trimmed.match(/^([^\s]+)(?:\s+(.+))?$/);
        if (!match) return `<span class="money">${trimmed}</span>`;

        const amount = match[1];
        const suffix = match[2] ? ` ${match[2]}` : '';
        return `<span class="money">${amount}</span>${suffix}`;
      }

      resolveMoneyHtml(planValue, plainFallback) {
        if (planValue) return planValue;
        if (plainFallback) return this.wrapPlainPriceAsMoneyHtml(plainFallback);
        return '';
      }

      setMoneyHtml(element, html) {
        if (!element || !html) return;

        const nextHtml = this.normalizeMoneyHtml(html);
        if (this.normalizeMoneyHtml(element.innerHTML) === nextHtml) return;

        element.innerHTML = nextHtml;
      }

      buildStateKey(isSubscription) {
        const plan = this.getSelectedPlan();

        return [
          this.getVariantId(),
          this.getPlanId(),
          isSubscription ? 'sub' : 'one',
          plan?.price_html || plan?.price_plain || '',
          plan?.compare_html || plan?.compare_plain || '',
          plan?.discount_percent || '',
          this._subifyReady ? 'ready' : 'pending',
        ].join('|');
      }

      syncSubscriptionDisplay() {
        const priceRoot = this.querySelector('[data-subscription-price-root]');
        const priceEl = this.querySelector('[data-subscription-price]');
        if (!priceRoot || !priceEl) return;

        const plan = this.getSelectedPlan();
        const nextPriceHtml = this.resolveMoneyHtml(plan?.price_html, plan?.price_plain);
        const nextCompareHtml = plan?.compare_html || plan?.compare_plain || null;
        let nextPercent = plan?.discount_percent || null;

        if (nextCompareHtml && nextPriceHtml && !nextPercent) {
          const temp = document.createElement('div');
          temp.innerHTML = nextCompareHtml;
          const compareText = temp.textContent.trim();
          temp.innerHTML = nextPriceHtml;
          const priceText = temp.textContent.trim();
          const compareNum = parseFloat(compareText.replace(/[^\d.]/g, ''));
          const priceNum = parseFloat(priceText.replace(/[^\d.]/g, ''));
          if (compareNum > priceNum) {
            nextPercent = Math.round(((compareNum - priceNum) / compareNum) * 100);
          }
        }

        this.setMoneyHtml(priceEl, nextPriceHtml);

        const compareWrap = this.querySelector('.product-purchase-price__subscription-compare-wrap');
        const compareEl = this.querySelector('[data-subscription-compare]');
        if (nextCompareHtml) {
          compareWrap?.classList.remove('hidden');
          this.setMoneyHtml(
            compareEl,
            nextCompareHtml.includes('<')
              ? nextCompareHtml
              : this.wrapPlainPriceAsMoneyHtml(nextCompareHtml)
          );
          priceRoot.classList.add('price--on-sale');
        } else {
          compareWrap?.classList.add('hidden');
          priceRoot.classList.remove('price--on-sale', 'price--show-badge');
        }

        let badgeEl = this.querySelector('[data-subscription-discount-badge]');
        if (nextCompareHtml && nextPercent && nextPercent > 0) {
          const badgeText = this.formatDiscountLabel(nextPercent);
          if (!badgeEl) {
            const templateBadge = this.onetimePanel?.querySelector('.price__badge-sale');
            badgeEl = document.createElement('span');
            badgeEl.className = templateBadge?.className || 'badge price__badge-sale';
            badgeEl.dataset.subscriptionDiscountBadge = '';
            if (templateBadge?.getAttribute('style')) {
              badgeEl.setAttribute('style', templateBadge.getAttribute('style'));
            }
            priceRoot.appendChild(badgeEl);
          }
          badgeEl.textContent = badgeText;
          priceRoot.classList.add('price--show-badge');
        } else {
          priceRoot.classList.remove('price--show-badge');
        }
      }

      showOnetimePanel() {
        this.dataset.activePanel = 'onetime';
        this.onetimePanel?.classList.remove('hidden');
        this.subscriptionPanel?.classList.add('hidden');
      }

      showSubscriptionPanel() {
        this.dataset.activePanel = 'subscription';
        this.onetimePanel?.classList.add('hidden');
        this.subscriptionPanel?.classList.remove('hidden');
      }

      update() {
        if (!this.isConnected) return;

        const hasSubscription = this.dataset.hasSubscription === 'true';
        const hasSubify = !!this.getSubifyWidget();
        const isSubscription = hasSubscription && hasSubify && this.isSubscriptionMode();
        const stateKey = this.buildStateKey(isSubscription);

        if (stateKey === this._lastStateKey) return;
        this._lastStateKey = stateKey;

        if (!hasSubscription || !hasSubify) {
          this.showOnetimePanel();
          return;
        }

        if (isSubscription) {
          this.syncSubscriptionDisplay();
          this.showSubscriptionPanel();
          return;
        }

        this.showOnetimePanel();
      }
    }
  );
}
